import { apiFetch } from '../client';
import type {
    TenantBrandingLocal,
    TenantUserDetailLocal,
    CreateTenantUserRequestLocal,
    CreateTenantUserResponseLocal,
    UpdateTenantUserRequestLocal,
    CreateTenantRoleRequestLocal,
    UpdateTenantRoleRequestLocal,
    AssignRoleRequestLocal,
    AssignRoleResponseLocal,
    AuditQueryParamsLocal,
    TenantSecurityPolicyLocal,
    UserFieldDefLocal,
    CreateUserFieldRequestLocal,
    UpdateUserFieldRequestLocal,
} from '../types/TenantLocal';
import type { UpdateTenantAutoregisterRequestLocal } from '../types/AutoregisterLocal';
import type {
    TenantUserListWire,
    TenantUserDetailWire,
    TenantRoleListWire,
    BrandingConfigWire,
    AuditQueryWire,
} from '../types/TenantWire';
import {
    normalizeTenantUserList,
    normalizeTenantUserDetail,
    normalizeTenantRoleList,
    normalizeBranding,
    normalizeAuditQuery,
} from '../types/tenantNormalizers';

interface ListParams {
    cursor?: string;
    pageSize?: number;
}

function toSearch(params: Record<string, string | number | undefined>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '';
    const usp = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
    return `?${usp.toString()}`;
}

interface RequestOpts {
    idempotencyKey?: string;
}

function withIdempotency(opts: RequestOpts): RequestInit {
    return opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {};
}

export const tenantApi = {
    // Users
    listUsers: (tid: string, params: ListParams = {}) =>
        apiFetch<TenantUserListWire>(`/tenants/${tid}/users${toSearch(params)}`).then(
            normalizeTenantUserList,
        ),
    getUser: (tid: string, cognitoSub: string): Promise<TenantUserDetailLocal> =>
        apiFetch<TenantUserDetailWire>(`/tenants/${tid}/users/${cognitoSub}`).then(
            normalizeTenantUserDetail,
        ),
    createUser: (tid: string, body: CreateTenantUserRequestLocal, opts: RequestOpts = {}) =>
        apiFetch<CreateTenantUserResponseLocal>(`/tenants/${tid}/users`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),
    updateUser: (tid: string, cognitoSub: string, body: UpdateTenantUserRequestLocal) =>
        apiFetch<TenantUserDetailLocal>(`/tenants/${tid}/users/${cognitoSub}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    disableUser: (tid: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/users/${cognitoSub}/disable`, {
            method: 'POST',
        }),
    deleteUser: (tid: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/users/${cognitoSub}`, {
            method: 'DELETE',
        }),
    enableUser: (tid: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/users/${cognitoSub}/enable`, {
            method: 'POST',
        }),
    resetMfa: (tid: string, cognitoSub: string) =>
        apiFetch<{ status: 'MFA_RESET' }>(`/tenants/${tid}/users/${cognitoSub}/mfa-reset`, {
            method: 'POST',
        }),
    resetPassword: (tid: string, cognitoSub: string) =>
        apiFetch<{ temporaryPassword: string }>(
            `/tenants/${tid}/users/${cognitoSub}/password-reset`,
            { method: 'POST' },
        ),
    // Reenvía la invitación (regenera contraseña temporal + manda el correo branded). Solo INVITED.
    resendInvitation: (tid: string, cognitoSub: string) =>
        apiFetch<{ status: 'INVITATION_RESENT' }>(
            `/tenants/${tid}/users/${cognitoSub}/resend-invitation`,
            { method: 'POST' },
        ),

    // Roles
    listRoles: (tid: string) =>
        apiFetch<TenantRoleListWire>(`/tenants/${tid}/roles`).then(normalizeTenantRoleList),
    createRole: (tid: string, body: CreateTenantRoleRequestLocal, opts: RequestOpts = {}) =>
        apiFetch<{ roleId: string }>(`/tenants/${tid}/roles`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),
    updateRole: (tid: string, roleId: string, body: UpdateTenantRoleRequestLocal) =>
        apiFetch<{ roleId: string }>(`/tenants/${tid}/roles/${roleId}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    deleteRole: (tid: string, roleId: string) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/roles/${roleId}`, { method: 'DELETE' }),

    // Role assignments
    assignRole: (tid: string, body: AssignRoleRequestLocal, opts: RequestOpts = {}) =>
        apiFetch<AssignRoleResponseLocal>(`/tenants/${tid}/role-assignments`, {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),
    // Revoca una asignación. El backend identifica la asignación por `assignmentId` en el path y
    // exige el `cognitoSub` del usuario dueño en el body (POST .../role-assignments/{id}/revoke).
    revokeRole: (tid: string, assignmentId: string, cognitoSub: string) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/role-assignments/${assignmentId}/revoke`, {
            method: 'POST',
            body: JSON.stringify({ cognitoSub }),
        }),

    // Branding
    getBranding: (tid: string): Promise<TenantBrandingLocal> =>
        apiFetch<BrandingConfigWire>(`/tenants/${tid}/branding`).then(normalizeBranding),
    updateBranding: (tid: string, body: TenantBrandingLocal): Promise<TenantBrandingLocal> =>
        apiFetch<BrandingConfigWire>(`/tenants/${tid}/branding`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }).then(normalizeBranding),

    // Audit
    queryAudit: (tid: string, params: AuditQueryParamsLocal = {}) =>
        apiFetch<AuditQueryWire>(
            `/tenants/${tid}/audit${toSearch(params as Record<string, string | number | undefined>)}`,
        ).then(normalizeAuditQuery),

    // Security policy (métodos MFA permitidos del tenant). Gateados por
    // tenant.security.policy.view (GET) / tenant.security.policy.manage (PUT).
    getSecurityPolicy: (tid: string): Promise<TenantSecurityPolicyLocal> =>
        apiFetch<TenantSecurityPolicyLocal>(`/tenants/${tid}/security-policy`),
    updateSecurityPolicy: (
        tid: string,
        body: TenantSecurityPolicyLocal,
    ): Promise<TenantSecurityPolicyLocal> =>
        apiFetch<TenantSecurityPolicyLocal>(`/tenants/${tid}/security-policy`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    // Autoregistro (DEC-RBAC-050). Operación SOLO-platform: gateada por
    // `platform.tenant.autoregister.manage` (scope PLATFORM, sin scopeFromRequest). El backend re-enforza.
    // NO hay GET de la config vigente (TenantDetail no la incluye) → el editor no pre-carga; este PUT es
    // merge-preserve del lado del backend. `allowedRoles` es la allowlist anti-escalación (DEC-RBAC-038):
    // el backend valida que cada roleId ⊆ roles del tenant.
    updateAutoregister: (tid: string, body: UpdateTenantAutoregisterRequestLocal) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/autoregister`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),

    // User fields (catálogo de campos personalizados del tenant). Gateados server-side.
    listUserFields: (tid: string): Promise<UserFieldDefLocal[]> =>
        apiFetch<UserFieldDefLocal[]>(`/tenants/${tid}/user-fields`),
    createUserField: (tid: string, body: CreateUserFieldRequestLocal) =>
        apiFetch<UserFieldDefLocal>(`/tenants/${tid}/user-fields`, {
            method: 'POST',
            body: JSON.stringify(body),
        }),
    updateUserField: (tid: string, key: string, body: UpdateUserFieldRequestLocal) =>
        apiFetch<UserFieldDefLocal>(`/tenants/${tid}/user-fields/${key}`, {
            method: 'PUT',
            body: JSON.stringify(body),
        }),
    deleteUserField: (tid: string, key: string) =>
        apiFetch<{ success: true }>(`/tenants/${tid}/user-fields/${key}`, { method: 'DELETE' }),
};
